import { LAYOUT_TYPE } from "../../constants/pageConstants/dynamicHomeConstants";

/**
 * @typedef {"image"|"video"|"lottie"} LcpMediaType
 */

/**
 * @typedef {{ url: string, mediaType: LcpMediaType }} LcpAsset
 */

/**
 * Returns a stable key for a grid/carousel cell.
 * @param {object} cell - Cell object from layout or content.
 * @param {string} fallback - Fallback key when cell has no id.
 * @returns {string}
 */
function getCellKey(cell, fallback) {
  return cell?.cellId || cell?.id || fallback;
}

/**
 * Returns true when the CMS widget name/key contains "lcp" (case-insensitive).
 * @param {object|null|undefined} widget - Widget configuration object.
 * @returns {boolean}
 */
export function isLcpNamedWidget(widget) {
  const label = (widget?.name ?? widget?.key ?? "").toLowerCase();
  return label.includes("lcp");
}

/**
 * Resolves media URL and type from a cell using the same rules as GridCell / MediaCell.
 * @param {object} cell - Merged cell object.
 * @returns {LcpAsset|null}
 */
export function resolveCellMedia(cell) {
  if (!cell || typeof cell !== "object") {
    return null;
  }

  const mediaUrl =
    cell.imageUrl || cell.mediaUrl || (cell.background?.type === "image" ? cell.background.value : undefined);
  if (!mediaUrl || typeof mediaUrl !== "string") {
    return null;
  }

  let mediaType = cell.mediaType;
  if (mediaType === "image" || mediaType == null) {
    mediaType = mediaUrl.includes(".mp4") ? "video" : "image";
  }

  if (mediaType === "lottie") {
    return { url: mediaUrl, mediaType: "lottie" };
  }
  if (mediaType === "video") {
    return { url: mediaUrl, mediaType: "video" };
  }
  if (mediaType === "image") {
    return { url: mediaUrl, mediaType: "image" };
  }

  return null;
}

/**
 * Builds a content lookup map for product-collection grids.
 * @param {Array<object>} content - Widget content array.
 * @returns {Map<string, object>}
 */
function buildContentMap(content) {
  const map = new Map();
  (content ?? []).forEach((item, index) => {
    const key = getCellKey(item, `content-${index}`);
    map.set(key, item);
  });
  return map;
}

/**
 * Merges layout cell config with optional content overrides.
 * @param {object} cell - Layout cell.
 * @param {Map<string, object>} contentMap - Content keyed by cell id.
 * @param {string} fallbackKey - Fallback key for the cell.
 * @returns {object}
 */
function mergeCell(cell, contentMap, fallbackKey) {
  const key = getCellKey(cell, fallbackKey);
  const fromContent = contentMap.get(key);
  return fromContent ? { ...cell, ...fromContent } : cell;
}

/**
 * Collects ordered cells from a widget layout snapshot.
 * @param {object} widget - Widget configuration object.
 * @returns {Array<object>}
 */
export function getWidgetCells(widget) {
  if (!widget?.layoutSnapshot) {
    return [];
  }

  const layoutType = widget.layoutSnapshot.type;
  const contentMap = buildContentMap(widget.content ?? widget.typeConfig?.content ?? []);

  if (layoutType === LAYOUT_TYPE.GRID) {
    const rows = widget.layoutSnapshot.gridConfig?.rows ?? [];
    return rows.flatMap((row, rowIndex) =>
      (row?.cells ?? []).map((cell, cellIndex) => mergeCell(cell, contentMap, `${row.id ?? rowIndex}-${cellIndex}`)),
    );
  }

  if (layoutType === LAYOUT_TYPE.CAROUSEL) {
    const rows = widget.layoutSnapshot.carouselConfig?.grid?.rows ?? [];
    return rows
      .flatMap((row) => row?.cells ?? [])
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter((cell) => cell.imageUrl || cell.mediaUrl);
  }

  return [];
}

/**
 * Returns true when LCP eager/priority hints should apply to a carousel cell.
 * Only the first slide is prioritized — remaining slides stay lazy.
 * @param {number} index - Zero-based slide index in display order.
 * @param {boolean} isLcpHint - Whether the parent widget is LCP-tagged.
 * @returns {boolean}
 */
export function shouldPrioritizeCarouselCell(index, isLcpHint) {
  return Boolean(isLcpHint && index === 0);
}

/**
 * @param {object} widget - Widget configuration object.
 * @returns {LcpAsset|null}
 */
export function extractLcpAssetFromWidget(widget) {
  if (!isLcpNamedWidget(widget)) {
    return null;
  }

  const cells = getWidgetCells(widget);
  for (const cell of cells) {
    const asset = resolveCellMedia(cell);
    if (asset) {
      return asset;
    }
  }

  return null;
}

/**
 * Normalizes feed items (`{ widget }`) or raw widget arrays into widget objects.
 * @param {Array<object>|null|undefined} feedItems
 * @returns {Array<object>}
 */
export function normalizeFeedWidgets(feedItems) {
  if (!Array.isArray(feedItems)) {
    return [];
  }

  return feedItems.map((item) => item?.widget ?? item).filter(Boolean);
}

/**
 * Extracts the first LCP asset from feed widgets whose name contains "lcp".
 * Only one asset is returned so multiple high-priority preloads never compete.
 * @param {Array<object>|null|undefined} feedItems - Feed items or widget objects.
 * @returns {LcpAsset|null}
 */
export function extractFirstLcpAssetFromFeed(feedItems) {
  const widgets = normalizeFeedWidgets(feedItems);

  for (const widget of widgets) {
    const asset = extractLcpAssetFromWidget(widget);
    if (asset) {
      return asset;
    }
  }

  return null;
}

/**
 * Resolves widgets for SSR preload from desktop or mobile home feed payloads.
 * @param {object|null|undefined} serverPageData - Home page feed API response.
 * @param {boolean} isMobile - Whether the request is mobile.
 * @returns {Array<object>}
 */
export function getHomeFeedWidgetsForPreload(serverPageData, isMobile) {
  if (!serverPageData) {
    return [];
  }

  if (!isMobile && Array.isArray(serverPageData.widgets)) {
    return normalizeFeedWidgets(serverPageData.widgets);
  }

  const stores = serverPageData.stores;
  if (!Array.isArray(stores) || stores.length === 0) {
    return [];
  }

  const selectedStore = stores.find((store) => store.isSelected) ?? stores[0];
  return normalizeFeedWidgets(selectedStore?.feed?.widgets);
}
